import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs';

import { SasSpinnerComponent } from '@components';
import { SasSpinnerService } from '@services';
import { SasBreadcrumbComponent } from '../sas-breadcrumb/sas-breadcrumb.component';
import { SasHeaderComponent } from '../sas-header/sas-header.component';
import { SasMenuComponent } from '../sas-menu/sas-menu.component';
import { SasMenuService } from '../sas-menu/sas-menu.service';
import { RouteAnnouncerService } from './route-announcer.service';

export const ROUTE_DATA_HIDE_BREADCRUMB = 'hideBreadcrumb';

@Component({
    selector: 'sas-shell',
    imports: [RouterOutlet, SasMenuComponent, SasHeaderComponent, SasSpinnerComponent, SasBreadcrumbComponent],
    templateUrl: './sas-shell.component.html',
    // Layout styles are global (html, body, #app, routed views): libs/styles/src/sass/_layout-shell.scss.
    // Box-less host so the layout renders exactly as when it lived directly in app-root.
    host: { style: 'display: contents' }
})
export class SasShellComponent {
    private readonly viewContent = viewChild<ElementRef>('viewContent');
    private readonly mainContent = viewChild<ElementRef<HTMLElement>>('mainContent');

    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    readonly menuService = inject(SasMenuService);
    readonly spinnerService = inject(SasSpinnerService);
    readonly hideBreadcrumb = signal(false);
    // Pages loaded inside another application (iframe) hide the menu and header.
    readonly isEmbedded = window.location.href.includes('/embedded/');

    private lastPath = '';

    constructor() {
        // ngb default 'hover focus' leaves tooltips stuck open after a mouse click (the element keeps focus).
        inject(NgbTooltipConfig).triggers = 'hover';
        inject(RouteAnnouncerService).start();

        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed())
            .subscribe(event => {
                this.resetScrollOnPathChange(event);

                let route = this.activatedRoute.firstChild;
                while (route?.firstChild) {
                    route = route.firstChild;
                }

                if (route?.snapshot) {
                    this.hideBreadcrumb.set(route.snapshot.data?.[ROUTE_DATA_HIDE_BREADCRUMB] === true);
                }
            });
    }

    // Query-param-only changes (filters, paging) keep the scroll position
    private resetScrollOnPathChange(event: NavigationEnd): void {
        const path = event.urlAfterRedirects.split(/[?#]/)[0];
        if (path === this.lastPath) {return;}
        this.lastPath = path;

        // Desktop scrolls inside <main>; mobile scrolls the window
        this.mainContent()?.nativeElement.scrollTo({ top: 0, behavior: 'instant' });
        window.scrollTo({ top: 0, behavior: 'instant' });
    }

    skipToContent(event?: KeyboardEvent): void {
        // Only focus on content for Enter or Space key presses or if no event is provided (clicking the link)
        if (event?.code === 'Enter' || event?.code === 'Space' || event === undefined) {
            event?.preventDefault();
            this.viewContent()?.nativeElement.focus();
        }
    }
}
